import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { ToastProvider } from './components/Toast/Toast';
import { Loader } from './components/Loader/Loader';
import Navigation from './components/Navigation/Navigation';
import Footer from './components/Footer/Footer';
import CancelBooking from './components/CancelBooking';
import InAppNotification from './components/InAppNotification';
import './styles/global.css';
import { messages } from './utils/messages';

// PWA сервисы
import { registerServiceWorker, requestNotificationPermission, subscribeToPush, isPushSupported } from './utils/pushService';

const Header = lazy(() => import('./components/Header/Header'));
const Main = lazy(() => import('./components/Main/Main'));
const About = lazy(() => import('./components/About/About'));
const Catalog = lazy(() => import('./components/Catalog/Catalog'));
const Gallery = lazy(() => import('./components/Gallery/Gallery'));
const Prices = lazy(() => import('./components/Prices/Prices'));
const Coaches = lazy(() => import('./components/Coaches/Coaches'));
const Certificates = lazy(() => import('./components/Certificates/Certificates'));
const Offer = lazy(() => import('./components/Offer/Offer'));
const News = lazy(() => import('./components/News/News'));
const SummerCamp = lazy(() => import('./components/SummerCamp/SummerCamp'));
const Schedule = lazy(() => import('./components/Schedule/Schedule'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
};

const NotFound = () => (
  <div style={{ padding: '4rem 2rem', textAlign: 'center', minHeight: '50vh' }}>
    <h2 style={{ fontSize: '3rem', color: '#3b82f6', marginBottom: '1rem' }}>404</h2>
    <p style={{ fontSize: '1.2rem', color: '#4a5568', marginBottom: '2rem' }}>Страница не найдена</p>
    <a href="/" style={{ padding: '0.8rem 2rem', background: '#3b82f6', color: 'white', borderRadius: '50px', textDecoration: 'none' }}>
      Вернуться на главную
    </a>
  </div>
);

function AppContent() {
  // ===== АВТОМАТИЧЕСКАЯ ПРОВЕРКА ВЕРСИИ =====
  useEffect(() => {
    const checkAndReload = async () => {
      try {
        const res = await fetch("/version.json?t=" + Date.now());
        const data = await res.json();
        const currentVersion = data.version;
        const savedVersion = localStorage.getItem("appVersion");
        if (savedVersion && savedVersion !== currentVersion) {
          console.log("🔄 Новая версия:", currentVersion);
          localStorage.setItem("appVersion", currentVersion);
          if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map(key => caches.delete(key)));
          }
          window.location.reload();
          return;
        }
        if (!savedVersion) {
          localStorage.setItem("appVersion", currentVersion);
        }
      } catch (e) {}
    };
    checkAndReload();
  }, []);

  // Инициализация PWA
  useEffect(() => {
    const initPWA = async () => {
      if (!isPushSupported()) return;
      const registration = await registerServiceWorker();
      if (registration) {
        const hasPermission = await requestNotificationPermission();
        if (hasPermission) {
          await subscribeToPush(registration);
        }
      }
    };
    initPWA();
  }, []);

  // Автоматическое обновление PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const checkForUpdates = async () => {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
        console.log('🔍 Проверка обновлений PWA');
      };
      
      // Проверка при загрузке
      checkForUpdates();
      
      // Проверка каждые 6 часов
      const interval = setInterval(checkForUpdates, 6 * 60 * 60 * 1000);
      
      // При смене Service Worker (новое обновление)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('🔄 Новая версия PWA, перезагрузка...');
        window.location.reload();
      });
      
      return () => clearInterval(interval);
    }
  }, []);

  return (
    <div className="app" style={{ paddingTop: '80px' }}>
      <Navigation />
      <main className="main-content">
        <Suspense fallback={<Loader fullScreen />}>
          <Routes>
            <Route path="/" element={<><Header /><Main /></>} />
            <Route path="/about" element={<About />} />
            <Route path="/coaches" element={<Coaches />} />
            <Route path="/summer-camp" element={<SummerCamp />} />
            <Route path="/certificates" element={<Certificates />} />
            <Route path="/offer" element={<Offer />} />
            <Route path="/news" element={<News />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/prices" element={<Prices />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/cancel/:bookingId" element={<CancelBooking />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <InAppNotification />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <ToastProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <AppContent />
          </Router>
        </ToastProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;